import type { AnalyticsDriver, AnalyticsEventParameters } from './analyticsController';

type ConsentValue = 'granted' | 'denied';

export interface GoogleAnalyticsConsentSettings {
  analytics_storage: ConsentValue;
  ad_storage: ConsentValue;
  ad_user_data: ConsentValue;
  ad_personalization: ConsentValue;
}

export interface GoogleTagRuntime {
  initialize(measurementId: string, consent: GoogleAnalyticsConsentSettings): void;
  setConsent(consent: GoogleAnalyticsConsentSettings): void;
  setCollectionEnabled(measurementId: string, enabled: boolean): void;
  setUserId(measurementId: string, userId: string | null): void;
  logEvent(
    measurementId: string,
    eventName: string,
    parameters?: Record<string, unknown>,
  ): void;
}

interface GoogleAnalyticsDriverOptions {
  measurementId: string;
  enabled: boolean;
  runtime: GoogleTagRuntime;
  locationOrigin: string;
  clearCookies: () => void;
  onError?: (error: unknown) => void;
}

type GoogleTagCommandArguments = IArguments;
type GoogleTagWindow = Window & typeof globalThis & {
  dataLayer?: GoogleTagCommandArguments[];
};

type GoogleTagCommand = (...args: unknown[]) => void;

const createGoogleTagCommand = (browserWindow: GoogleTagWindow): GoogleTagCommand => {
  browserWindow.dataLayer = browserWindow.dataLayer || [];
  return function googleTagCommand(..._args: unknown[]) {
    browserWindow.dataLayer?.push(arguments);
  };
};

const setGoogleAnalyticsDisabled = (
  browserWindow: GoogleTagWindow,
  measurementId: string,
  disabled: boolean,
): void => {
  (browserWindow as unknown as Record<string, unknown>)[`ga-disable-${measurementId}`] = disabled;
};

export const createBrowserGoogleTagRuntime = (
  browserWindow: Window & typeof globalThis,
  browserDocument: Document,
): GoogleTagRuntime => {
  const tagWindow = browserWindow as GoogleTagWindow;
  const gtag = createGoogleTagCommand(tagWindow);

  return {
    initialize(measurementId, consent) {
      gtag('consent', 'default', consent);
      gtag('js', new Date());
      gtag('config', measurementId, {
        send_page_view: false,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      });

      if (!browserDocument.head.querySelector('[data-imabird-google-analytics]')) {
        const script = browserDocument.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
        script.setAttribute('data-imabird-google-analytics', measurementId);
        browserDocument.head.appendChild(script);
      }
    },

    setConsent(consent) {
      gtag('consent', 'update', consent);
    },

    setCollectionEnabled(measurementId, enabled) {
      setGoogleAnalyticsDisabled(tagWindow, measurementId, !enabled);
    },

    setUserId(measurementId, userId) {
      gtag('config', measurementId, {
        user_id: userId,
        send_page_view: false,
        update: true,
      });
    },

    logEvent(measurementId, eventName, parameters) {
      gtag('event', eventName, {
        ...parameters,
        send_to: measurementId,
      });
    },
  };
};

const isGoogleAnalyticsCookie = (name: string): boolean =>
  name === '_ga'
  || name.startsWith('_ga_')
  || name === '_gid'
  || name === '_gat'
  || name.startsWith('_gat_')
  || name.startsWith('_gac_')
  || name.startsWith('_gcl_');

export const clearGoogleAnalyticsCookies = (
  cookieDocument: Pick<Document, 'cookie'>,
  hostname: string,
): void => {
  const cookieNames = cookieDocument.cookie
    .split(';')
    .map((cookie) => cookie.split('=', 1)[0].trim())
    .filter(isGoogleAnalyticsCookie);

  const hostnameParts = hostname.split('.').filter(Boolean);
  const registrableDomain = hostnameParts.length >= 2
    ? hostnameParts.slice(-2).join('.')
    : hostname;

  cookieNames.forEach((name) => {
    cookieDocument.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
    if (hostname) {
      cookieDocument.cookie = `${name}=; Max-Age=0; path=/; Domain=${hostname}; SameSite=Lax`;
    }
    if (registrableDomain && registrableDomain !== hostname) {
      cookieDocument.cookie = `${name}=; Max-Age=0; path=/; Domain=.${registrableDomain}; SameSite=Lax`;
    }
  });
};

const deniedConsent: GoogleAnalyticsConsentSettings = {
  analytics_storage: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
};

const grantedAnalyticsConsent: GoogleAnalyticsConsentSettings = {
  ...deniedConsent,
  analytics_storage: 'granted',
};

export const createGoogleAnalyticsDriver = (
  options: GoogleAnalyticsDriverOptions,
): AnalyticsDriver => {
  let initialized = false;

  return {
    async enable() {
      if (!options.enabled || !options.measurementId) return false;

      try {
        options.runtime.setCollectionEnabled(options.measurementId, true);
        if (!initialized) {
          options.runtime.initialize(options.measurementId, grantedAnalyticsConsent);
          initialized = true;
        } else {
          options.runtime.setConsent(grantedAnalyticsConsent);
        }
        return true;
      } catch (error) {
        options.onError?.(error);
        return false;
      }
    },

    disable() {
      if (initialized) {
        options.runtime.setConsent(deniedConsent);
        options.runtime.setUserId(options.measurementId, null);
        options.runtime.setCollectionEnabled(options.measurementId, false);
      }
      options.clearCookies();
    },

    identify(userId) {
      if (initialized) {
        options.runtime.setUserId(options.measurementId, userId);
      }
    },

    trackPageView(pagePath, pageTitle) {
      if (initialized) {
        options.runtime.logEvent(options.measurementId, 'page_view', {
          page_path: pagePath,
          page_location: `${options.locationOrigin}${pagePath}`,
          page_title: pageTitle,
        });
      }
    },

    trackEvent(eventName: string, parameters?: AnalyticsEventParameters) {
      if (initialized) {
        options.runtime.logEvent(options.measurementId, eventName, parameters);
      }
    },
  };
};
